import { type ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { Notify } from "@/components/ui/notify";

interface ModalOverlayProps {
  onClose: () => void;
  /** Nome acessível do diálogo (Radix exige). Renderizado sr-only; o header visível vai em children. */
  title: string;
  children: ReactNode;
  /** Classes do Card interno (largura/altura padrão de modal já aplicadas). */
  className?: string;
  /** Em modais de formulário: ESC/clique-fora pedem confirmação antes de descartar o que foi digitado. */
  confirmDismiss?: boolean;
}

// Overlay de modal padrão da plataforma (review agendamento p3 + code review). Sobre Radix Dialog:
// ganha focus-trap, scroll-lock, focus-restore, portal e — crucial — empilhamento de camadas, então
// ESC/clique-fora só fecham a camada do topo (um Popover de calendário ou um confirm aninhado não
// derruba o modal). `open` é constante: quem renderiza o ModalOverlay controla a montagem; aqui só
// interceptamos a tentativa de dismiss para opcionalmente confirmar o descarte.
export function ModalOverlay({
  onClose,
  title,
  children,
  className,
  confirmDismiss,
}: ModalOverlayProps) {
  async function attemptClose() {
    if (confirmDismiss) {
      const ok = await Notify.confirm({
        variant: "danger",
        title: "Descartar alterações?",
        description: "As informações preenchidas neste formulário serão perdidas.",
        confirmLabel: "Descartar",
        cancelLabel: "Continuar editando",
      });
      if (!ok) return;
    }
    onClose();
  }
  return (
    <Dialog.Root
      open
      onOpenChange={(o) => {
        if (!o) void attemptClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-background/80 motion-safe:animate-fade-in" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2"
        >
          <Dialog.Title className="sr-only">{title}</Dialog.Title>
          <Card
            className={cn(
              "flex max-h-[88vh] w-[92vw] max-w-2xl animate-scale-in flex-col overflow-hidden shadow-rd-xl",
              className
            )}
          >
            {children}
          </Card>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
